import React, { useMemo } from "react";
import { Box } from "@mui/material";
import WorkLog from "../../models/WorkLog";
import { Token } from "../../theme";

interface Props {
  rows: WorkLog[];
  isTimeOff: (row: WorkLog) => boolean;
  isAssignationActive: (row: WorkLog) => boolean;
  onDayClick: (row: WorkLog) => void;
}

type DayState = "weekend" | "holiday" | "timeoff" | "paused" | "missing" | "partial" | "filled";

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const columnFor = (date: Date) => (date.getDay() + 6) % 7;

const stateOf = (row: WorkLog, isTimeOff: (r: WorkLog) => boolean, isAssignationActive: (r: WorkLog) => boolean): DayState => {
  if (row.isWeekend) return "weekend";
  if (row.isHoliday) return "holiday";
  if (isTimeOff(row)) return "timeoff";
  if (row.isPaused || !isAssignationActive(row)) return "paused";
  if (!row.externalId) return "missing";
  if (!row.hours) return "missing";
  return "filled";
};

const CELL: Record<DayState, { bg: string; border: string; label?: string; labelColor?: string }> = {
  weekend: { bg: Token.surfaceSunken, border: Token.borderSubtle },
  holiday: { bg: Token.statusInfoBg, border: Token.borderSubtle, label: "Holiday", labelColor: Token.statusInfoFg },
  timeoff: { bg: Token.timeoffBg, border: Token.borderSubtle, label: "Time off", labelColor: Token.timeoffFg },
  paused: { bg: Token.surfaceSunken, border: Token.borderSubtle, label: "Not assigned", labelColor: Token.textTertiary },
  missing: { bg: Token.statusWarningBg, border: Token.statusWarningFg },
  partial: { bg: Token.surfaceCard, border: Token.borderDefault },
  filled: { bg: Token.surfaceCard, border: Token.borderDefault },
};

export const WorklogCalendar: React.FC<Props> = ({ rows, isTimeOff, isAssignationActive, onDayClick }) => {
  const cells = useMemo(() => {
    if (rows.length === 0) return [];
    const leading = columnFor(rows[0].shortDay);
    return [...Array(leading).fill(null), ...rows];
  }, [rows]);

  return (
    <Box
      style={{
        background: Token.surfaceCard,
        border: `1px solid ${Token.borderDefault}`,
        borderRadius: 8,
        padding: 12,
      }}
    >
      <Box style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            style={{
              fontSize: 10.5,
              textTransform: "uppercase",
              letterSpacing: ".08em",
              color: Token.textTertiary,
              fontWeight: 600,
              padding: "0 4px 6px",
            }}
          >
            {day}
          </div>
        ))}
      </Box>

      <Box style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 6 }}>
        {cells.map((row: WorkLog | null, index: number) => {
          if (!row) return <div key={`pad-${index}`} />;

          const state = stateOf(row, isTimeOff, isAssignationActive);
          const style = CELL[state];
          const clickable = state === "missing";

          return (
            <div
              key={row.shortDay.toISOString()}
              onClick={clickable ? () => onDayClick(row) : undefined}
              style={{
                minHeight: 78,
                border: `1px solid ${style.border}`,
                borderRadius: 5,
                background: style.bg,
                padding: 8,
                display: "flex",
                flexDirection: "column",
                cursor: clickable ? "pointer" : "default",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontVariantNumeric: "tabular-nums",
                  fontSize: 12,
                  color: state === "weekend" ? Token.textTertiary : Token.textPrimary,
                }}
              >
                {row.shortDay.getDate()}
              </div>

              {state === "missing" && (
                <div style={{ fontSize: 11, fontWeight: 600, color: Token.statusWarningFg, marginTop: "auto" }}>+ Log hours</div>
              )}

              {state === "filled" && (
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontVariantNumeric: "tabular-nums",
                    fontSize: 17,
                    fontWeight: 600,
                    color: Token.textPrimary,
                    marginTop: "auto",
                  }}
                >
                  {row.hours}
                </div>
              )}

              {style.label && <div style={{ fontSize: 11, color: style.labelColor, marginTop: "auto" }}>{style.label}</div>}
            </div>
          );
        })}
      </Box>
    </Box>
  );
};
